import { useMemo, useState } from 'react';
import { Chess } from 'chess.js';
import { legalMovesFromPosition } from '../chess/game.js';

const PIECES = {
  wp: '♟',
  wn: '♞',
  wb: '♝',
  wr: '♜',
  wq: '♛',
  wk: '♚',
  bp: '♟',
  bn: '♞',
  bb: '♝',
  br: '♜',
  bq: '♛',
  bk: '♚'
};

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

function squareName(row, column) {
  return `${FILES[column]}${8 - row}`;
}

/** Rook start square for a standard castling SAN (king-to-rook click UX). */
function castlingRookSquare(move) {
  if (move.san !== 'O-O' && move.san !== 'O-O-O') {
    return null;
  }
  const rank = move.from[1];
  return move.san === 'O-O' ? `h${rank}` : `a${rank}`;
}

export default function ChessBoard({
  game,
  orientation = 'white',
  disabled = false,
  onMove,
  checkLabel = ''
}) {
  const [selected, setSelected] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const chess = useMemo(() => new Chess(game.fen), [game.fen]);
  const rows = chess.board();
  const viewRows = orientation === 'black'
    ? [...rows].reverse().map((row) => [...row].reverse())
    : rows;
  const candidates = useMemo(
    () => (selected && game.status === 'playing'
      ? legalMovesFromPosition(chess, selected)
      : []),
    [chess, game.status, selected]
  );
  // Map clickable squares (king dest + castling rook) to the engine move to send.
  const clickTargets = useMemo(() => {
    const map = new Map();
    for (const move of candidates) {
      const existing = map.get(move.to) || [];
      existing.push(move);
      map.set(move.to, existing);
      const rookSquare = castlingRookSquare(move);
      if (rookSquare) {
        map.set(rookSquare, [move]);
      }
    }
    return map;
  }, [candidates]);
  const destinations = useMemo(() => new Set(clickTargets.keys()), [clickTargets]);

  function actualSquare(viewRow, viewColumn) {
    const row = orientation === 'black' ? 7 - viewRow : viewRow;
    const column = orientation === 'black' ? 7 - viewColumn : viewColumn;
    return squareName(row, column);
  }

  function chooseSquare(square, piece) {
    if (disabled) {
      return;
    }
    if (selected && destinations.has(square)) {
      const matching = clickTargets.get(square) || [];
      if (matching.some((move) => move.promotion)) {
        setPromotion({ from: selected, to: matching[0].to });
      } else {
        const move = matching[0];
        onMove({ from: move.from, to: move.to, promotion: move.promotion || 'q' });
      }
      setSelected(null);
      return;
    }

    const expectedColor = game.turn === 'white' ? 'w' : 'b';
    setSelected(piece?.color === expectedColor ? square : null);
  }

  function finishPromotion(piece) {
    onMove({ ...promotion, promotion: piece });
    setPromotion(null);
  }

  return (
    <div className="board-wrap">
      <div className="chessboard" role="grid" aria-label="Chess board">
        {viewRows.flatMap((row, viewRow) =>
          row.map((piece, viewColumn) => {
            const square = actualSquare(viewRow, viewColumn);
            const baseRow = orientation === 'black' ? 7 - viewRow : viewRow;
            const baseColumn = orientation === 'black' ? 7 - viewColumn : viewColumn;
            const dark = (baseRow + baseColumn) % 2 === 1;
            const lastMove = game.moves.at(-1);
            const isLastMove = lastMove && (lastMove.from === square || lastMove.to === square);
            return (
              <button
                type="button"
                role="gridcell"
                aria-label={`${square}${piece ? ` ${piece.color}${piece.type}` : ''}`}
                className={[
                  'square',
                  dark ? 'dark' : 'light',
                  selected === square ? 'selected' : '',
                  destinations.has(square) ? 'destination' : '',
                  isLastMove ? 'last-move' : ''
                ].join(' ')}
                key={square}
                onClick={() => chooseSquare(square, piece)}
              >
                <span className={`piece ${piece?.color || ''} piece-${piece?.type || 'empty'}`}>
                  {piece ? PIECES[`${piece.color}${piece.type}`] : ''}
                </span>
                {viewColumn === 0 && <small className="rank-label">{square[1]}</small>}
                {viewRow === 7 && <small className="file-label">{square[0]}</small>}
              </button>
            );
          })
        )}
      </div>

      {checkLabel ? (
        <p className="check-popup" role="status">{checkLabel}</p>
      ) : null}

      {promotion && (
        <div className="promotion-picker" role="dialog" aria-label="Choose promotion piece">
          <p>Promote pawn to</p>
          <div>
            {['q', 'r', 'b', 'n'].map((piece) => (
              <button type="button" key={piece} onClick={() => finishPromotion(piece)}>
                {PIECES[`${game.turn === 'white' ? 'w' : 'b'}${piece}`]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
